/**
 * P-C 蓝图→数字人接缝(2026-07-21):location.state.explosive 验型。
 *
 * 手法照抄 poster/model/blueprint.ts(一次性 ref 消费 + 逐字段验型,脏 state 整包丢弃
 * 不预载);console 不调 /blueprints/{id}/use —— 取用登记由 backend 建任务派发成功后
 * 自动 best-effort(防双计,对齐视频/海报口径)。
 */

export interface DhExplosiveHandoff {
  blueprintId: number;
  formulaName: string;
  payload: Record<string, unknown>;
  /** 2026-08-14「参考片在拍什么」:逐拍画面对口播线有用(哪句该切什么产品画面)。 */
  breakdown?: import("@/entities/explosive").ExplosiveBreakdown | null;
  sourcePlatform?: string;
  /** BPWB-1(2026-09-03):蓝图留了原片 → 工作卡当场能听参考片的语气节奏。 */
  hasRawVideo?: boolean;
}

/**
 * 参考片逐字口播拼纯文本(去时间戳;空行/畸形行剔除);无口播返回 ""。
 *
 * 2026-10-10 起只用来判「有没有口播」:有 → 进页调 C1 逐句分析,进逐句对照编辑区;
 * 无 → 退回「按公式生成脚本」。不再整段塞进文案框(没有逐句、参考片事实无任何标记)。
 */
export function transcriptTextOf(payload: Record<string, unknown>): string {
  const t = payload.transcript;
  if (!Array.isArray(t)) return "";
  return t
    .map((l) =>
      l && typeof l === "object" && typeof (l as { text?: unknown }).text === "string"
        ? (l as { text: string }).text.trim()
        : ""
    )
    .filter(Boolean)
    .join("\n");
}

/**
 * 产品节点 → 「产品事实」文本(适配我的产品弹层的预填;2026-08-10)。
 *
 * 这段文本就是 blueprint-script 的 `userInput` —— ai 侧 prompt 明令「不得编造用户产品
 * 没有的卖点或任何数字」,事实给得越实,重写越贴。逐字取自产品库,用户在弹层里可改。
 */
export function productFactsText(node: {
  name: string;
  sellingPoints?: string[] | null;
  attributes?: Record<string, unknown> | null;
  summary?: string | null;
}): string {
  const lines = [`产品:${node.name}`];
  const pts = (node.sellingPoints ?? []).map((s) => s.trim()).filter(Boolean);
  if (pts.length) lines.push(`卖点:${pts.join(";")}`);
  const specs: string[] = [];
  for (const [label, raw] of Object.entries(node.attributes ?? {})) {
    // 标量才敢当规格念出去(对象/数组是内部结构);上限 8 条,口播不是参数表
    if (typeof raw !== "string" && typeof raw !== "number") continue;
    const value = String(raw).trim();
    if (!label.trim() || !value) continue;
    specs.push(`${label.trim()} ${value}`);
    if (specs.length >= 8) break;
  }
  if (specs.length) lines.push(`参数:${specs.join(";")}`);
  const summary = (node.summary ?? "").trim();
  if (summary) lines.push(`说明:${summary.slice(0, 200)}`);
  return lines.join("\n");
}

/** location.state.explosive 逐字段验型;形状不对返回 null(整包丢弃,页面按无蓝图渲染)。 */
export function parseDhExplosiveState(state: unknown): DhExplosiveHandoff | null {
  if (!state || typeof state !== "object") return null;
  const ex = (state as { explosive?: unknown }).explosive;
  if (!ex || typeof ex !== "object") return null;
  const o = ex as Record<string, unknown>;
  // codex 修:正整数安全域(0/负数/小数/超 2^53 一律整包丢弃)
  if (typeof o.blueprintId !== "number" || !Number.isSafeInteger(o.blueprintId)) return null;
  if (o.blueprintId <= 0) return null;
  if (typeof o.formulaName !== "string" || !o.formulaName.trim()) return null;
  if (!o.payload || typeof o.payload !== "object" || Array.isArray(o.payload)) return null;
  return {
    blueprintId: o.blueprintId,
    formulaName: o.formulaName,
    payload: o.payload as Record<string, unknown>,
    // 只验「是个对象」:breakdown 是 ai 侧自由形状,逐键验型会把新增字段挡在门外
    ...(o.breakdown && typeof o.breakdown === "object"
      ? {
          breakdown: o.breakdown as import("@/entities/explosive").ExplosiveBreakdown,
        }
      : {}),
    sourcePlatform: typeof o.sourcePlatform === "string" ? o.sourcePlatform : undefined,
    // 只认真布尔(同 video 侧口径):说大话的代价是给用户一个点了 404 的播放位
    hasRawVideo: o.hasRawVideo === true,
  };
}
